import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useMediaUrl } from "../../context/MediaUrlContext";
import { sameSide } from "../../utils/sideMatch";
import {
  X,
  ZoomIn,
  ZoomOut,
  Download,
  Expand,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

// The extension the file is actually saved with — read from the URL, since
// that's known before any fetch is attempted (and a fetch is exactly what
// can fail and need a fallback). This source mixes .jpg, .jpeg and
// occasionally other formats — a ROI image, for one, comes back .jpeg — so
// hardcoding one, as this used to, could save a file whose extension
// doesn't match its actual content, which endpoint security on a
// locked-down Windows machine is far more likely than a personal Mac to
// flag or silently drop.
const getFileExtension = (url) => {
  const match = /\.([a-zA-Z0-9]+)(?:[?#]|$)/.exec(url || "");
  return match ? match[1].toLowerCase() : "jpg";
};

const ImageLightbox = ({
  isOpen,
  onClose,
  images,
  currentIndex,
  onImageChange,
  assessmentData, // Added to access all_damages and roi_images
  isDamageImages = false, // Added to determine if these are damage images
}) => {
  const resolveMediaUrl = useMediaUrl();
  const touchStartRef = useRef(null);
  const selectedThumbRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(currentIndex || 0);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  // Which toolbar action was last used — the highlight follows it instead of
  // sitting on Zoom In permanently.
  const [lastActiveTool, setLastActiveTool] = useState(null);

  // A part photo can be small enough that scale(zoomLevel) on a max-width/
  // max-height-capped <img> barely moves it — the box was already smaller
  // than its cap, so zooming in had nothing to expand into. Ported from the
  // current design: below it, zooming instead grows the image's actual box
  // (width/height), up to whatever fits the viewport.
  const [naturalDimensions, setNaturalDimensions] = useState({
    width: 0,
    height: 0,
  });

  const isSmall = useMemo(() => {
    return (
      naturalDimensions.width > 0 &&
      naturalDimensions.height > 0 &&
      (naturalDimensions.width < 400 || naturalDimensions.height < 400)
    );
  }, [naturalDimensions]);

  const baseMultiplier = useMemo(() => {
    if (!isSmall || naturalDimensions.width === 0) return 1.5;
    const scaleX = (window.innerWidth * 0.8) / naturalDimensions.width;
    const scaleY = (window.innerHeight * 0.75) / naturalDimensions.height;
    return Math.max(1.5, Math.min(scaleX, scaleY, 4.0));
  }, [isSmall, naturalDimensions]);

  useEffect(() => {
    console.log("showAnnotations changed to:", showAnnotations);
  }, [showAnnotations]);

  useEffect(() => {
    setSelectedIndex(currentIndex || 0);
  }, [currentIndex]);

  // The strip scrolls horizontally but nothing kept the highlighted thumb
  // inside it — paging with the arrow keys or the on-screen chevrons moved
  // the selection past whatever the strip happened to already show, with no
  // indication of where the new selection actually sat.
  useEffect(() => {
    selectedThumbRef.current?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedIndex]);

  useEffect(() => {
    if (onImageChange) {
      onImageChange(selectedIndex);
    }
  }, [selectedIndex, onImageChange]);

  const handlePrevious = useCallback(() => {
    if (images && images.length > 0) {
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    }
  }, [images]);

  const handleNext = useCallback(() => {
    if (images && images.length > 0) {
      setSelectedIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    }
  }, [images]);

  const handleThumbnailClick = (index) => {
    setSelectedIndex(index);
  };

  // Function to get the appropriate image URL based on annotation state
  const getImageUrl = useCallback(
    (image) => {
      if (!isDamageImages || !assessmentData || showAnnotations) {
        return image.imageUrl || image.src;
      }

      // An image that already carries its un-annotated original needs no
      // lookup. The current design checks this first; this one did not.
      if (image.originalImageUrl) {
        return image.originalImageUrl;
      }

      // Otherwise pair the damage photograph with its ROI original by side.
      //
      // This compared `roi.side === image.side` exactly, and the two
      // collections do not agree on spelling — front_right against
      // frontRight, back_left against rear_left. The match returned nothing,
      // the code fell through to the image it already had, and the toggle
      // looked like it did nothing at all. The current design has normalised
      // this all along, which is why it worked there and not here.
      if (
        image.source === "all_damages" &&
        assessmentData.roi_images &&
        image.side
      ) {
        const roiImage = assessmentData.roi_images.find((roi) =>
          sameSide(roi.side, image.side),
        );

        if (roiImage && roiImage.upload_picture) {
          return resolveMediaUrl(roiImage.upload_picture);
        }
      }

      // Fallback to original image
      return image.imageUrl || image.src;
    },
    [isDamageImages, assessmentData, showAnnotations, resolveMediaUrl]
  );

  // Zoom and pan functions
  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev * 1.2, 3)); // Max zoom 3x
    setLastActiveTool("zoomIn");
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => {
      const newZoom = prev / 1.2;
      if (newZoom < 1) {
        // Reset position when zooming out to original size
        setImagePosition({ x: 0, y: 0 });
        return 1;
      }
      return newZoom;
    });
    setLastActiveTool("zoomOut");
  }, []);

  // Reset zoom and position when changing images
  useEffect(() => {
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
    setNaturalDimensions({ width: 0, height: 0 });
  }, [selectedIndex]);

  // Fullscreen functionality
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
    setLastActiveTool("expand");
  }, []);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Download functionality
  const handleDownload = useCallback(async () => {
    const currentImage = images[selectedIndex];
    const imageUrl = getImageUrl(currentImage);
    const fileName = `image-${currentImage?.side || selectedIndex + 1}.${getFileExtension(imageUrl)}`;

    const clickDownloadLink = (href) => {
      const link = document.createElement("a");
      link.href = href;
      link.download = fileName;
      // Only matters when download is ignored (the direct-URL fallback, on
      // a server that forces Content-Disposition: inline) — without it,
      // that click navigates this tab itself to the image, leaving the app.
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    try {
      const response = await fetch(imageUrl);
      // A blocked or proxied request (a corporate network filter rewriting
      // the response, an expired session on the media host) can still
      // resolve without throwing — an HTML error page, not the photo. Only
      // response.ok tells the two apart; blob() alone accepts either.
      if (!response.ok) {
        throw new Error(`Image fetch failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      clickDownloadLink(objectUrl);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      // fetch() reads the response body, which is what CORS actually gates —
      // a plain <a download> pointed straight at the file is not a script
      // read, so it saves normally even where fetch() was blocked (a
      // network filter, a CORS policy some environments don't send; this is
      // reportedly the case on some Windows machines but not Mac, on the
      // exact same deployment). It only fails to save, rather than throw, if
      // the server itself forces Content-Disposition: inline — nothing left
      // to try client-side at that point, so it opens the image instead of
      // leaving the button looking like it did nothing.
      console.warn(
        "Blob download blocked, falling back to a direct link:",
        error,
      );
      clickDownloadLink(imageUrl);
    }
    setLastActiveTool("download");
  }, [images, selectedIndex, getImageUrl]);

  // Mouse drag functionality for panning
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - imagePosition.x,
        y: e.clientY - imagePosition.y,
      });
    }
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging && zoomLevel > 1) {
        setImagePosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    },
    [isDragging, zoomLevel, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Wheel zoom functionality
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoomLevel((prev) => {
      const newZoom = prev * delta;
      if (newZoom < 1) {
        setImagePosition({ x: 0, y: 0 });
        return 1;
      }
      return Math.min(newZoom, 3);
    });
  };

  useEffect(() => {
    const keyDownHandler = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        handleNext();
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        handleZoomIn();
      } else if (e.key === "-") {
        e.preventDefault();
        handleZoomOut();
      } else if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.key === "d" || e.key === "D") {
        e.preventDefault();
        handleDownload();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", keyDownHandler);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
      document.body.style.overflow = "unset";
    };
  }, [
    isOpen,
    onClose,
    handlePrevious,
    handleNext,
    handleZoomIn,
    handleZoomOut,
    toggleFullscreen,
    handleDownload,
  ]);

  if (!isOpen || !images || images.length === 0) {
    return null;
  }

  const currentImage = images[selectedIndex];

  return (
    <div
      className="fixed inset-0 z-[70] backdrop-blur-[5px] backdrop-filter bg-black bg-opacity-75"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Main Container */}
      <div
        className="relative w-full h-full flex flex-col items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Toolbar */}
        <div className="absolute top-0 left-0 right-0 bg-black bg-opacity-45 h-[47px] z-10">
          <div className="flex items-center justify-end h-full px-4">
            {/* Right side - Controls */}
            <div className="flex shrink-0 items-center">
              {/* Annotations Toggle - Only show for damage images */}
              {isDamageImages && (
                <button
                  className="flex shrink-0 items-center gap-1 border-r border-border-dark px-2 py-2 sm:px-4"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log(
                      "Annotation toggle clicked, changing from:",
                      showAnnotations,
                      "to:",
                      !showAnnotations
                    );
                    setShowAnnotations(!showAnnotations);
                  }}
                >
                  <span
                    className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold text-[#4c5055] text-xs uppercase tracking-[-0.1px]"
                    style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                  >
                    Annotations • {showAnnotations ? "ON" : "OFF"}
                  </span>
                  <div className="overflow-clip relative shrink-0 w-12 h-12 flex items-center justify-center">
                    <div className="absolute inset-[8.33%]">
                      <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 100 100"
                        fill="none"
                        className={`transition-colors duration-200 ${
                          showAnnotations ? "text-primary" : "text-[#4c5055]"
                        }`}
                      >
                        {/* Toggle switch background */}
                        <rect
                          x="10"
                          y="35"
                          width="80"
                          height="30"
                          rx="15"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        {/* Toggle switch circle */}
                        <circle
                          cx={showAnnotations ? "75" : "25"}
                          cy="50"
                          r="12"
                          fill="currentColor"
                          className="transition-all duration-200"
                        />
                      </svg>
                    </div>
                  </div>
                </button>
              )}

              {/* Zoom In */}
              <button
                className="shrink-0 p-2 hover:bg-black hover:bg-opacity-20 sm:p-3"
                onClick={(e) => {
                  e.stopPropagation();
                  handleZoomIn();
                }}
                title="Zoom In"
              >
                <ZoomIn
                  size={20}
                  className={
                    lastActiveTool === "zoomIn" ? "text-primary" : "text-[#4c5055]"
                  }
                />
              </button>

              {/* Zoom Out */}
              <button
                className="shrink-0 p-2 hover:bg-black hover:bg-opacity-20 sm:p-3"
                onClick={(e) => {
                  e.stopPropagation();
                  handleZoomOut();
                }}
                title="Zoom Out"
              >
                <ZoomOut
                  size={20}
                  className={
                    lastActiveTool === "zoomOut" ? "text-primary" : "text-[#4c5055]"
                  }
                />
              </button>

              {/* Expand/Fullscreen */}
              <button
                className="shrink-0 p-2 hover:bg-black hover:bg-opacity-20 sm:p-3"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFullscreen();
                }}
                title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
              >
                <Expand
                  size={20}
                  className={
                    lastActiveTool === "expand" ? "text-primary" : "text-[#4c5055]"
                  }
                />
              </button>

              {/* Download */}
              <button
                className="shrink-0 p-2 hover:bg-black hover:bg-opacity-20 sm:p-3"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
                title="Download Image"
              >
                <Download
                  size={20}
                  className={
                    lastActiveTool === "download" ? "text-primary" : "text-[#4c5055]"
                  }
                />
              </button>

              {/* Close */}
              <button
                className="shrink-0 p-2 hover:bg-black hover:bg-opacity-20 sm:p-3"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
              >
                <X size={20} className="text-[#4c5055]" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Image Display */}
        <div
          className="relative flex-1 flex items-center justify-center w-full px-12 py-20"
          /* Swipe between images. There is no hover on a touch screen, so the
             arrows above are the only affordance and a swipe is what a reader
             actually reaches for. Ignored while zoomed, where a drag is a pan,
             and ignored for multi-touch so a pinch is never a page turn. */
          onTouchStart={(e) => {
            if (e.touches.length !== 1) return;
            touchStartRef.current = {
              x: e.touches[0].clientX,
              y: e.touches[0].clientY,
            };
          }}
          onTouchEnd={(e) => {
            const start = touchStartRef.current;
            touchStartRef.current = null;
            if (!start || zoomLevel > 1 || images.length < 2) return;
            const touch = e.changedTouches[0];
            const dx = touch.clientX - start.x;
            const dy = touch.clientY - start.y;
            // Horizontal, and decisively so — otherwise it was a scroll.
            if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
            if (dx < 0) handleNext();
            else handlePrevious();
          }}
        >
          {/* Navigation Arrow - Left */}
          {images.length > 1 && (
            <button
              type="button"
              aria-label="Previous image"
              title="Previous image (left arrow)"
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-45 rounded-sm p-3 shadow-lg transition-colors hover:bg-opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              onClick={(e) => {
                e.stopPropagation();
                handlePrevious();
              }}
            >
              <ChevronLeft size={22} className="text-white" />
            </button>
          )}

          {/* Main Image */}
          <div
            className="relative max-w-full max-h-full overflow-hidden"
            onWheel={handleWheel}
            style={{
              cursor:
                zoomLevel > 1 ? (isDragging ? "grabbing" : "grab") : "default",
            }}
          >
            <img
              key={`main-image-${selectedIndex}-${showAnnotations}`}
              src={getImageUrl(currentImage)}
              alt={
                currentImage?.alt ||
                currentImage?.side ||
                `Image ${selectedIndex + 1}`
              }
              className="max-w-full max-h-full object-contain rounded-lg transition-transform duration-200"
              style={{
                width: (isSmall && zoomLevel > 1)
                  ? `${naturalDimensions.width * (1 + (zoomLevel - 1) * baseMultiplier)}px`
                  : "auto",
                height: (isSmall && zoomLevel > 1)
                  ? `${naturalDimensions.height * (1 + (zoomLevel - 1) * baseMultiplier)}px`
                  : "auto",
                maxHeight: (isSmall && zoomLevel > 1) ? "none" : "75vh",
                maxWidth: (isSmall && zoomLevel > 1) ? "none" : "90vw",
                transform: (isSmall && zoomLevel > 1)
                  ? `translate(${imagePosition.x}px, ${imagePosition.y}px)`
                  : `scale(${zoomLevel}) translate(${
                      imagePosition.x / zoomLevel
                    }px, ${imagePosition.y / zoomLevel}px)`,
                transformOrigin: "center center",
              }}
              onLoad={(e) => {
                const { naturalWidth, naturalHeight } = e.target;
                setNaturalDimensions({ width: naturalWidth, height: naturalHeight });
              }}
              onMouseDown={handleMouseDown}
              draggable={false}
            />
          </div>

          {/* Navigation Arrow - Right */}
          {images.length > 1 && (
            <button
              type="button"
              aria-label="Next image"
              title="Next image (right arrow)"
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-45 rounded-sm p-3 shadow-lg transition-colors hover:bg-opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
            >
              <ChevronRight size={22} className="text-white" />
            </button>
          )}
        </div>

        {/* Image Label and Zoom Indicator */}
        <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2">
          <div
            className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold text-white text-base tracking-[-0.32px] text-center"
            style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
          >
            {currentImage?.title ||
              currentImage?.side ||
              `Image ${selectedIndex + 1}`}
          </div>
          {zoomLevel > 1 && (
            <div className="text-white text-sm text-center mt-1 opacity-75">
              {Math.round(zoomLevel * 100)}% zoom
            </div>
          )}
        </div>

        {/* Bottom Thumbnail Strip */}
        {images.length > 1 && (
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-45 max-h-[100px]">
            <div className="h-[100px] px-4 overflow-x-auto">
              {/* justify-center belongs on this row, not the scrolling
                  container around it — centering the scroll container itself
                  only "centers" the overflow by pushing its start half past
                  scrollLeft 0, which a browser won't scroll back into (no
                  negative scrollLeft in LTR). That half of the strip was
                  reachable by neither a manual scroll nor scrollIntoView,
                  which is why paging forward past the end and wrapping to
                  the first thumbnails could select one with no way to bring
                  it on screen. w-max/min-w-full keeps a short list centered
                  (matches the current design's own fix for this) while a
                  list wider than the container just starts at its own edge,
                  same as any ordinary scrollable row. */}
              <div className="flex w-max min-w-full h-full gap-2 items-center justify-center">
                {images.map((image, index) => (
                  <button
                    key={index}
                    ref={index === selectedIndex ? selectedThumbRef : null}
                    className={`relative flex-shrink-0 w-[100px] h-[80px] rounded overflow-hidden border-2 transition-all ${
                      index === selectedIndex
                        ? "border-primary opacity-100"
                        : "border-transparent opacity-50 hover:opacity-75"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleThumbnailClick(index);
                    }}
                  >
                    <img
                      key={`thumb-${index}-${showAnnotations}`}
                      src={getImageUrl(image)}
                      alt={
                        image?.alt || image?.side || `Thumbnail ${index + 1}`
                      }
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageLightbox;
